import { loadOemPdfRenderer } from './pdf-renderer';
import { useState } from 'react';
import { Download } from 'lucide-react';
import {
  checkedCustomerSnapshot,
  oemCustomerHtml,
  type OemCustomerSnapshot,
} from '@sqi/document-core';
import { prepareExport, type OemWorkspace } from '../../lib/oem-client';
import { runtimeMode, type OemContext } from '../../lib/auth';
function download(blob: Blob, name: string) {
  const href = URL.createObjectURL(blob),
    a = document.createElement('a');
  a.href = href;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
}
export async function renderOemCustomerPdf(value: unknown, deck = false): Promise<Blob> {
  const s = checkedCustomerSnapshot(value);
  const { Document, Page, StyleSheet, Text, View, pdf } = await loadOemPdfRenderer();
  const style = StyleSheet.create({
    page: {
      padding: 36,
      fontFamily: 'Noto Sans Thai',
      fontSize: 11,
      color: '#20392f',
      backgroundColor: '#fbf8f0',
      lineHeight: 1.55,
    },
    title: { fontSize: deck ? 28 : 20, fontWeight: 700, marginBottom: 12 },
    heading: { fontSize: 16, fontWeight: 700, marginTop: 18, marginBottom: 8 },
    caption: { fontSize: 9, color: '#68746a' },
    footer: {
      position: 'absolute',
      bottom: 18,
      left: 36,
      right: 36,
      fontSize: 8,
      color: '#68746a',
    },
    body: { marginBottom: 12 },
    section: { marginBottom: 16 },
  });
  const footer = (
    <View fixed style={style.footer}>
      <Text>
        {s.proposal.id} v{s.proposal.version} · {s.quote.id} v{s.quote.version}
      </Text>
      <Text render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
    </View>
  );
  const doc = (
    <Document
      title={`${s.project.label} · Customer ${deck ? 'deck' : 'proposal'} v${s.proposal.version}`}
      author="SQI OEM local test"
      subject={s.disclaimer}
    >
      <Page
        size={deck ? 'A4' : 'A4'}
        orientation={deck ? 'landscape' : 'portrait'}
        style={style.page}
        wrap
      >
        <Text style={style.title}>{s.project.label}</Text>
        <Text>{s.project.brand ?? 'แบรนด์ยังไม่ระบุ'}</Text>
        <Text style={style.caption}>{s.disclaimer}</Text>
        <Text style={style.caption}>Target {s.sourceTargetId} · exact reviewed snapshot</Text>
        {!deck
          ? s.sections.map((section, i) => (
              <View key={i} style={style.section} wrap>
                <Text style={style.heading}>{section.heading}</Text>
                <Text style={style.body}>{section.body}</Text>
                {section.factIds.map((id) => (
                  <Text key={id}>{s.facts.find((f) => f.id === id)?.value}</Text>
                ))}
              </View>
            ))
          : null}
        {!deck ? (
          <>
            <Text style={style.heading}>ใบเสนอราคาและเงื่อนไข</Text>
            {s.quote.lines.map((line, i) => (
              <Text key={i} style={style.body}>
                {line.skuId} · {line.quantity} × {line.unitPrice} {line.currency} · tax {line.tax}
              </Text>
            ))}
            <Text>{s.quote.terms}</Text>
            <Text>
              ใช้ได้ถึง {s.quote.validUntil} · {s.quote.destination}
            </Text>
            <Text style={style.heading}>เวอร์ชันและหลักฐานที่ใช้อ้างอิง</Text>
            {s.references.map((r) => (
              <Text key={r.id} style={style.caption}>
                {r.kind} v{r.version} · {r.id} · {r.hash}
              </Text>
            ))}
          </>
        ) : null}
        {footer}
      </Page>
      {deck
        ? s.sections.map((section, i) => (
            <Page key={i} size="A4" orientation="landscape" style={style.page} wrap>
              <Text style={style.title}>{section.heading}</Text>
              <Text style={style.body}>{section.body}</Text>
              {section.factIds.map((id) => (
                <Text key={id} style={style.body}>
                  {s.facts.find((f) => f.id === id)?.value}
                </Text>
              ))}
              <Text style={style.caption}>{s.disclaimer}</Text>
              {footer}
            </Page>
          ))
        : null}
      {deck ? (
        <Page size="A4" orientation="landscape" style={style.page} wrap>
          <Text style={style.title}>ราคา เงื่อนไข และ exact version</Text>
          {s.quote.lines.map((line, i) => (
            <Text key={i}>
              {line.skuId} · {line.quantity} × {line.unitPrice} {line.currency}
            </Text>
          ))}
          <Text style={style.body}>{s.quote.terms}</Text>
          <Text style={style.caption}>
            Quote {s.quote.id} v{s.quote.version} · {s.quote.hash}
          </Text>
          <Text style={style.caption}>Target {s.sourceTargetId}</Text>
          <Text style={style.caption}>{s.disclaimer}</Text>
          {footer}
        </Page>
      ) : null}
    </Document>
  );
  return pdf(doc).toBlob();
}
export async function customerExportZip(
  snapshot: OemCustomerSnapshot,
  identity: { id: string; hash: string },
) {
  const { default: JSZip } = await import('jszip');
  const zip = new JSZip(),
    files = [
      { path: 'customer-proposal.pdf', blob: await renderOemCustomerPdf(snapshot) },
      { path: 'customer-deck.pdf', blob: await renderOemCustomerPdf(snapshot, true) },
      {
        path: 'customer-snapshot.json',
        blob: new Blob([JSON.stringify(snapshot, null, 2)], { type: 'application/json' }),
      },
      {
        path: 'customer-preview.html',
        blob: new Blob([oemCustomerHtml(snapshot)], { type: 'text/html' }),
      },
    ];
  const manifest = [];
  for (const file of files) {
    const bytes = await file.blob.arrayBuffer();
    zip.file(file.path, bytes);
    const sha256 = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)))
      .map((x) => x.toString(16).padStart(2, '0'))
      .join('');
    manifest.push({ path: file.path, size: bytes.byteLength, sha256 });
  }
  zip.file(
    'manifest.json',
    JSON.stringify(
      {
        formatVersion: 'oem-customer-export-v2',
        snapshotId: identity.id,
        snapshotHash: identity.hash,
        proposal: snapshot.proposal,
        quote: {
          id: snapshot.quote.id,
          version: snapshot.quote.version,
          hash: snapshot.quote.hash,
        },
        files: manifest,
      },
      null,
      2,
    ),
  );
  return zip.generateAsync({ type: 'blob' });
}
export function CustomerExport({
  context,
  workspace,
}: {
  readonly context: OemContext;
  readonly workspace: OemWorkspace;
}) {
  const [proposal, setProposal] = useState(''),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [prepared, setPrepared] = useState<{
      id: string;
      hash: string;
      snapshot: OemCustomerSnapshot;
    } | null>(null);
  const choices = workspace.records.filter((r) => r.kind === 'proposal');
  async function prepare() {
    setBusy(true);
    setError('');
    try {
      const result = await prepareExport(context.projectId, proposal, crypto.randomUUID());
      setPrepared({ ...result, snapshot: checkedCustomerSnapshot(result.snapshot) });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'export_unavailable');
    } finally {
      setBusy(false);
    }
  }
  async function output(kind: 'proposal' | 'deck' | 'zip') {
    if (!prepared) return;
    setBusy(true);
    try {
      const blob =
        kind === 'zip'
          ? await customerExportZip(prepared.snapshot, prepared)
          : await renderOemCustomerPdf(prepared.snapshot, kind === 'deck');
      download(blob, `SQI-${prepared.id}-${kind}.${kind === 'zip' ? 'zip' : 'pdf'}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'render_unavailable');
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="card">
      <div className="eyebrow">REVIEWED CUSTOMER EXPORT</div>
      <h2>Proposal และ deck ฉบับลูกค้า</h2>
      <p>
        อ้างถึงข้อเท็จจริงและเวอร์ชันที่ได้รับการตรวจ เนื้อหาที่เตรียมแล้วคงอยู่เมื่อมี successor
      </p>
      {runtimeMode === 'fixture' ? (
        <p className="note-warning">
          ส่งออกฉบับ reviewed ใช้ connected mode และผลการตรวจจริงใน local harness
        </p>
      ) : (
        <>
          {context.role === 'auditor' || context.actions.length === 0 ? (
            <p className="scope">สิทธิ์อ่านหลักฐานเท่านั้น · ไม่สามารถสร้าง snapshot ใหม่</p>
          ) : null}
          <label>
            Proposal exact version
            <select
              value={proposal}
              onChange={(e) => {
                setProposal(e.target.value);
                setPrepared(null);
              }}
            >
              <option value="">เลือกฉบับที่ทบทวนแล้ว</option>
              {choices.map((r) => (
                <option key={r.id} value={r.id}>
                  {String(r.content.label)} v{r.version}
                </option>
              ))}
            </select>
          </label>
          <button
            className="action secondary"
            disabled={
              busy ||
              !proposal ||
              context.role === 'auditor' ||
              context.actions.length === 0 ||
              !context.readBundles.includes('commercial') ||
              !navigator.onLine
            }
            onClick={() => void prepare()}
          >
            ตรวจและเตรียม snapshot
          </button>
          {prepared ? (
            <>
              <p className="version">
                Snapshot {prepared.id} · {prepared.hash}
              </p>
              <div className="actions">
                {(['proposal', 'deck', 'zip'] as const).map((k) => (
                  <button key={k} className="action" disabled={busy} onClick={() => void output(k)}>
                    <Download />
                    {k === 'proposal'
                      ? 'PDF proposal'
                      : k === 'deck'
                        ? 'PDF deck'
                        : 'ZIP ฉบับลูกค้า'}
                  </button>
                ))}
              </div>
              <div className="customer-export-preview">
                <h3>{prepared.snapshot.project.label}</h3>
                {prepared.snapshot.sections.map((s, i) => (
                  <section key={i}>
                    <h4>{s.heading}</h4>
                    <p>{s.body}</p>
                  </section>
                ))}
                <p className="scope">{prepared.snapshot.disclaimer}</p>
              </div>
            </>
          ) : null}
        </>
      )}
      {error ? (
        <p className="note-danger" role="alert">
          {error}
        </p>
      ) : null}
    </section>
  );
}
